import {Currency, CurrencyAmount} from "util/currency";
import {
  BookingField,
  BookingsFilters,
  LandlordBooking, LandlordBookingBookingsStats, LandlordBookingDealStats,
  LandlordBookingEarningStats, LandlordBookingRevenueStats,
  LandlordBookingStats,
  LandlordRevenuePeriod
} from "services/entities/booking";
import {DateRange, DateTime, TimeSpan} from "util/time";
import {formatQueryString} from "util/url";
import {callAPI} from "services/api";
import {readCurrency} from "services/currency";
import {PagedResult, QueryPagination} from "services/entities/queries";
import {addQueryFilters, readBooking} from "services/bookings";

function readLandlordBooking(data: any): LandlordBooking {
  const booking = readBooking(data);
  const bookingCurrency = readCurrency(data.currency);
  return {
    ...booking,
    price: CurrencyAmount.parse(data.price, bookingCurrency),
    tax: CurrencyAmount.parse(data.tax, bookingCurrency),
    totalPrice: CurrencyAmount.parse(data.total_price, bookingCurrency),
    landlordSplit: CurrencyAmount.parse(data.landlord_split, bookingCurrency),
    sookSplit: CurrencyAmount.parse(data.sook_split, bookingCurrency),
    outstandingAmount: data.outstanding_amount ? CurrencyAmount.parse(data.outstanding_amount, bookingCurrency) : null,
  } as LandlordBooking;
}

export async function loadLandlordBookings(filters?: BookingsFilters, pagination?: QueryPagination): Promise<PagedResult<LandlordBooking>> {
  const fields = [BookingField.Price, BookingField.Tax, BookingField.TotalPrice, BookingField.LandlordSplit,
    BookingField.SookSplit, BookingField.OutstandingAmount];
  const queryParams: {[index: string]: string} = {fields: fields.join(',')};
  if(filters) {
    addQueryFilters(queryParams, filters);
  }
  const pageSize = pagination ? pagination.pageSize : 100;
  const pageIndex = pagination?.pageIndex || 0;
  queryParams['limit'] = pageSize.toString();
  queryParams['offset'] = (pageIndex * pageSize).toString();
  const queryString = formatQueryString(queryParams);
  const response = await callAPI(`/bookings/?${queryString}`, 'GET', null, true);
  return {
    totalCount: response.count,
    pageIndex: pageIndex,
    results: response.results.map(readLandlordBooking)
  };
}

function readLandlordEarningsStats(data: any, currency: Currency): LandlordBookingEarningStats {
  return {
    subtotal: CurrencyAmount.parse(data.subtotal, currency),
    taxes: CurrencyAmount.parse(data.taxes, currency),
    total: CurrencyAmount.parse(data.total, currency),
  };
}

function readLandlordBookingDealStats(data: any): LandlordBookingDealStats {
  return {
    count: data.count,
    averageBookings: data.average_bookings
  };
}

function readLandlordBookingBookingsStats(data: any, currency: Currency): LandlordBookingBookingsStats {
  return {
    count: data.count,
    duration: TimeSpan.parse(data.duration),
    subtotal: CurrencyAmount.parse(data.subtotal, currency),
    taxes: CurrencyAmount.parse(data.taxes, currency),
    total: CurrencyAmount.parse(data.total, currency),
    averageDuration: TimeSpan.parse(data.average_duration),
    occupancyRate: data.occupancy_rate,
    vacancyRate: data.vacancy_rate
  };
}

function readLandlordBookingRevenueStats(data: any, currency: Currency): LandlordBookingRevenueStats {
  return {
    count: data.count,
    duration: TimeSpan.parse(data.duration),
    subtotal: CurrencyAmount.parse(data.subtotal, currency),
    taxes: CurrencyAmount.parse(data.taxes, currency),
    total: CurrencyAmount.parse(data.total, currency),
    averagePerMonth: CurrencyAmount.parse(data.average_per_month, currency),
    averagePerWeek: CurrencyAmount.parse(data.average_per_week, currency),
    averagePerDeal: CurrencyAmount.parse(data.average_per_deal, currency),
    averagePerBooking: CurrencyAmount.parse(data.average_per_booking, currency),
  };
}

export async function loadLandlordBookingsStats(dates: DateRange|{months: number[], years: number[]}): Promise<LandlordBookingStats> {
  const queryParams: {[index: string]: string[]|string} = {};
  if('start' in dates) {
    queryParams['start_date'] = dates.start.format('YYYY-MM-DD');
    queryParams['end_date'] = dates.end.format('YYYY-MM-DD');
  } else {
    queryParams['months'] = dates.months.map(m => (m + 1).toString());
    queryParams['years'] = dates.years.map(y => y.toString());
  }
  const queryString = formatQueryString(queryParams);
  const response = await callAPI(`/bookings/landlord-stats/?${queryString}`, 'GET', null, true);
  const currency = readCurrency(response.currency);
  return {
    bookings: readLandlordBookingBookingsStats(response.bookings, currency),
    deals: readLandlordBookingDealStats(response.deals),
    revenue: readLandlordBookingRevenueStats(response.revenue, currency),
    landlordSplit: readLandlordEarningsStats(response.landlord_split, currency),
    sookSplit: readLandlordEarningsStats(response.sook_split, currency)
  }
}

export async function downloadLandlordBookingsStats(dates: DateRange): Promise<Blob> {
  const queryParams: {[index: string]: string[]|string} = {};
  queryParams['start_date'] = dates.start.format('YYYY-MM-DD');
  queryParams['end_date'] = dates.end.format('YYYY-MM-DD');
  const queryString = formatQueryString(queryParams);
  return await callAPI(`/bookings/landlord-stats/download/?${queryString}`, 'GET', null, true);

}

function getPeriodRevenue(periods: {revenue: string|null, year: number, week: number}[], week: number, year: number, currency: Currency): CurrencyAmount {
  const p = periods.find(p => p.week === week && p.year === year);
  return p && p.revenue ? CurrencyAmount.parse(p.revenue, currency) : new CurrencyAmount(0, currency);
}

export async function loadLandlordBookingsRevenue(start: DateTime, end: DateTime): Promise<{currency: Currency, periods: LandlordRevenuePeriod[]}> {
  const queryParams: {[index: string]: string} = {};
  queryParams['start'] = start.format('YYYY-MM-DD');
  queryParams['end'] = end.format('YYYY-MM-DD');
  const queryString = formatQueryString(queryParams);
  const response = await callAPI(`/bookings/landlord-revenue/?${queryString}`, 'GET', null, true);
  const currency = readCurrency(response.currency);
  const periods = response.periods;
  const fullPeriods = [];
  let dt = start;
  const interval = new TimeSpan({weeks: 1});
  const endInterval = interval.subtract(new TimeSpan({days: 1}));
  while(dt.lessThan(end)) {
    fullPeriods.push({
      dates: { start: dt.clone(), end: dt.add(endInterval) },
      revenue: getPeriodRevenue(periods, dt.week, dt.year, currency)
    });
    dt = dt.add(interval);
  }
  return {currency: currency, periods: fullPeriods};
}
